import React from 'react';
import { signIn } from 'next-auth/react';
import { FormattedMessage } from 'react-intl';
import OAuthButtonsSlot from '~/jobiqo/components/Slots/OAuthButtonsSlot';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { setBrowserCookie } from '~/jobiqo/lib/session';
import { DIMENSIONS, EVENTS } from '~/jobiqo/lib/analytics';
import {
  passCustomFieldsDataToOAuthSignUp,
  useDisableSocialButtons,
} from '~/jobiqo/components/Slots/OAuthButtonsCustomFieldsSlot';
import { useFormContext } from 'react-hook-form';
import { GdprConsentInfo } from '~/jobiqo/types/types';
import { GdprFormSchema } from '../GDPRConsent/hooks/useGdprConsentContext';
import LinkedinColorIcon from '~/vcl/icons/color/Linkedin';
import GoogleColorIcon from '~/vcl/icons/color/Google';
import FacebookColorIcon from '~/vcl/icons/color/Facebook';
import EntraColorIcon from '~/vcl/icons/color/Entra';
import LinkedinIcon from '~/vcl/icons/Linkedin';
import GoogleIcon from '~/vcl/icons/Google';
import FacebookIcon from '~/vcl/icons/Facebook';

interface OAuthButtonsProps {
  /**
   * GDPR validation schema.
   */
  gdpr?: GdprFormSchema;
  /**
   * GDPR consent info.
   */
  consentInfos?: GdprConsentInfo[];
  /**
   * Create GDPR user consent.
   */
  createGdprUserConsent?: (mail: string) => void;
  /**
   * Optional role context to filter which OAuth buttons are shown.
   * - 'applicant': hides recruiter SSO buttons (Okta, Microsoft Entra ID).
   * - 'recruiter': shows only recruiter SSO buttons (Okta, Microsoft Entra ID).
   * - undefined (login page): shows all buttons.
   */
  role?: 'applicant' | 'recruiter';
}

const handleOAuthSignIn = (
  provider,
  createGdprUserConsent,
  values,
  consents,
) => {
  if (createGdprUserConsent) {
    createGdprUserConsent(values.email);
  }
  oauthSignIn(provider, values, consents);
};

const OAuthButtons = ({
  gdpr,
  consentInfos,
  createGdprUserConsent,
  role,
}: OAuthButtonsProps) => {
  const { APP } = useFrontendConfig();
  const { disableSocialButtons } = useDisableSocialButtons(gdpr, consentInfos);
  const { getValues } = useFormContext();
  const values = getValues();
  const consents = consentInfos;

  // When role is 'recruiter', only show SSO buttons (Okta, Microsoft Entra ID).
  // When role is 'applicant', hide the SSO buttons.
  // When no role (login page), show all buttons including the SSO buttons.
  const isRecruiterContext = role === 'recruiter';

  return (
    <>
      <OAuthButtonsSlot
        gdpr={gdpr}
        consentInfos={consentInfos}
        isRecruiterContext={isRecruiterContext}
      />
      {APP.SOCIAL_BUTTONS_ORDER.map((provider) => {
        if (
          provider === 'linkedin' &&
          APP.LINKEDIN_OAUTH_FEATURE_ACTIVE &&
          !isRecruiterContext
        ) {
          return (
            <>
              <Spacing />
              <Button
                buttonStyle="linkedin"
                type="button"
                disabled={disableSocialButtons}
                block
                onClick={() => {
                  handleOAuthSignIn(
                    'linkedin',
                    createGdprUserConsent,
                    values,
                    consents,
                  );
                }}
              >
                {APP.SOCIAL_ICONS_COLORED ? (
                  <LinkedinColorIcon />
                ) : (
                  <LinkedinIcon />
                )}{' '}
                <span>
                  <FormattedMessage
                    id="login_submit_linkedin"
                    defaultMessage={`Sign in with LinkedIn`}
                  />
                </span>
              </Button>
            </>
          );
        }
        if (
          provider === 'google' &&
          APP.GOOGLE_OAUTH_FEATURE_ACTIVE &&
          !isRecruiterContext
        ) {
          return (
            <>
              <Spacing />
              <Button
                buttonStyle="google"
                type="button"
                disabled={disableSocialButtons}
                block
                onClick={() => {
                  handleOAuthSignIn(
                    'google',
                    createGdprUserConsent,
                    values,
                    consents,
                  );
                }}
              >
                {APP.SOCIAL_ICONS_COLORED ? (
                  <GoogleColorIcon />
                ) : (
                  <GoogleIcon />
                )}{' '}
                <span>
                  <FormattedMessage
                    id="login_submit_google"
                    defaultMessage={`Sign in with Google`}
                  />
                </span>
              </Button>
            </>
          );
        }
        if (
          provider === 'facebook' &&
          APP.FACEBOOK_OAUTH_FEATURE_ACTIVE &&
          !isRecruiterContext
        ) {
          return (
            <>
              <Spacing />
              <Button
                buttonStyle="facebook"
                type="button"
                disabled={disableSocialButtons}
                block
                onClick={() => {
                  handleOAuthSignIn(
                    'facebook',
                    createGdprUserConsent,
                    values,
                    consents,
                  );
                }}
              >
                {APP.SOCIAL_ICONS_COLORED ? (
                  <FacebookColorIcon />
                ) : (
                  <FacebookIcon />
                )}{' '}
                <span>
                  <FormattedMessage
                    id="login_submit_facebook"
                    defaultMessage={`Sign in with Facebook`}
                  />
                </span>
              </Button>
            </>
          );
        }
        if (
          provider === 'okta' &&
          APP.OKTA_OAUTH_FEATURE_ACTIVE &&
          role !== 'applicant'
        ) {
          return (
            <>
              <Spacing />
              <Button
                buttonStyle="primary"
                type="button"
                disabled={disableSocialButtons}
                block
                onClick={() => {
                  handleOAuthSignIn(
                    'okta',
                    createGdprUserConsent,
                    values,
                    consents,
                  );
                }}
              >
                {APP.OKTA_SSO_BUTTON_LOGO && (
                  <img
                    src={APP.OKTA_SSO_BUTTON_LOGO}
                    alt=""
                    aria-hidden="true"
                    style={{ maxWidth: '50px', height: 'auto' }}
                  />
                )}{' '}
                <span>
                  <FormattedMessage
                    id="login_submit_okta_sso"
                    defaultMessage="Partner sign in with Okta"
                  />
                </span>
              </Button>
            </>
          );
        }
        if (
          provider === 'keycloak' &&
          APP.KEYCLOAK_OAUTH_FEATURE_ACTIVE &&
          role !== 'recruiter'
        ) {
          return (
            <>
              <Spacing />
              <Button
                buttonStyle="primary"
                type="button"
                disabled={disableSocialButtons}
                block
                onClick={() => {
                  handleOAuthSignIn(
                    'keycloak',
                    createGdprUserConsent,
                    values,
                    consents,
                  );
                }}
              >
                {APP.KEYCLOAK_SSO_BUTTON_LOGO && (
                  <img
                    src={APP.KEYCLOAK_SSO_BUTTON_LOGO}
                    alt=""
                    aria-hidden="true"
                    style={{ maxWidth: '50px', height: 'auto' }}
                  />
                )}{' '}
                <span>
                  <FormattedMessage
                    id="login_submit_keycloak_sso"
                    defaultMessage="Sign in with keycloak SSO"
                  />
                </span>
              </Button>
            </>
          );
        }
        if (
          provider === 'entra' &&
          APP.ENTRA_OAUTH_FEATURE_ACTIVE &&
          role !== 'applicant'
        ) {
          return (
            <>
              <Spacing />
              <Button
                buttonStyle="primary"
                type="button"
                disabled={disableSocialButtons}
                block
                onClick={() => {
                  handleOAuthSignIn(
                    'entra',
                    createGdprUserConsent,
                    values,
                    consents,
                  );
                }}
              >
                {APP.ENTRA_SSO_BUTTON_LOGO ? (
                  <img
                    src={APP.ENTRA_SSO_BUTTON_LOGO}
                    alt=""
                    aria-hidden="true"
                    style={{ maxWidth: '50px', height: 'auto' }}
                  />
                ) : (
                  <EntraColorIcon />
                )}{' '}
                <span>
                  <FormattedMessage
                    id="login_submit_entra_sso"
                    defaultMessage="Sign in with Microsoft Entra ID"
                  />
                </span>
              </Button>
            </>
          );
        }
        return null;
      })}
    </>
  );
};

function oauthSignIn(provider, values = {}, consents) {
  // Here we add the signup_method to the sessionStorage, this is used
  // for the signup_method GA4 dimension, this is cleared from the session
  // once the user gets to the dashboard.
  signIn(provider, {
    callbackUrl: `/dashboard?dispatch=${EVENTS.LOGIN.key}&${DIMENSIONS.signup_method}=${provider}`,
    prompt: 'login',
  }).then(() => {
    sessionStorage.setItem('signup_method', provider);

    // Pass custom fields data to the OAuth sign up.
    const customFieldsData = passCustomFieldsDataToOAuthSignUp(
      values,
      consents,
    );

    if (Object.keys(customFieldsData).length > 0) {
      // Set in session (no "expires" means session cookie).
      setBrowserCookie(
        'customProperties',
        JSON.stringify(customFieldsData),
        {},
      );
    }
  });
}

export default OAuthButtons;
